// Draws a plating card (M16) on a Canvas 2D context: title, the plate seen from above (or the side) with
// numbered elements, the legend, the camera angle (a small sketch), the plate, the light tip, the steps and
// the reference photo when there is one. Letter proportions (8.5 × 11), any size. Used for the on-screen
// preview, the PNG and (as an image) the PDF, so they always match.
import type { Bi } from '../../design/model/types';
import {
  ANGLE_TEXT,
  KIND_TEXT,
  PLATE_TEXT,
  type CameraAngle,
  type PlateElement,
  type PlatingCard,
} from './cards';

export type CardLang = 'en' | 'es' | 'both';
export const CARD_W = 1275; // Letter at 150 dpi
export const CARD_H = 1650;

type G = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

const FONT = 'system-ui, "Segoe UI", Roboto, Arial, sans-serif';
const text = (b: Bi, lang: CardLang) => (lang === 'both' ? `${b.es} / ${b.en}` : b[lang]);

/** Wraps text to `w`; returns the y after the last line. */
function para(
  g: G,
  s: string,
  x: number,
  y: number,
  w: number,
  size: number,
  color = '#222',
  weight = 400,
  lh = 1.3,
  draw = true,
): number {
  g.font = `${weight} ${size}px ${FONT}`;
  g.fillStyle = color;
  g.textBaseline = 'top';
  const words = s.split(/\s+/);
  let line = '';
  for (const wd of words) {
    const cand = line ? `${line} ${wd}` : wd;
    if (line && g.measureText(cand).width > w) {
      if (draw) g.fillText(line, x, y);
      y += size * lh;
      line = wd;
    } else line = cand;
  }
  if (line) {
    if (draw) g.fillText(line, x, y);
    y += size * lh;
  }
  return y;
}

function plateShape(g: G, card: PlatingCard, S: number) {
  const c = S / 2;
  g.save();
  g.lineWidth = S * 0.006;
  switch (card.plate) {
    case 'round':
      g.fillStyle = '#ffffff';
      g.strokeStyle = '#c9c9c9';
      g.beginPath();
      g.arc(c, c, S * 0.47, 0, Math.PI * 2);
      g.fill();
      g.stroke();
      g.beginPath();
      g.arc(c, c, S * 0.37, 0, Math.PI * 2);
      g.strokeStyle = '#e4e4e4';
      g.stroke();
      break;
    case 'bowl':
      g.fillStyle = '#f4f1ea';
      g.strokeStyle = '#b9b2a6';
      g.beginPath();
      g.arc(c, c, S * 0.47, 0, Math.PI * 2);
      g.fill();
      g.stroke();
      g.fillStyle = '#e9e3d8';
      g.beginPath();
      g.arc(c, c, S * 0.4, 0, Math.PI * 2);
      g.fill();
      break;
    case 'board':
    case 'slate':
    case 'tray':
    case 'basket': {
      const col = { board: '#c79a63', slate: '#3a3d40', tray: '#d8d2c6', basket: '#c9a46a' }[card.plate];
      g.fillStyle = col;
      g.beginPath();
      g.roundRect(S * 0.05, S * 0.12, S * 0.9, S * 0.76, S * 0.04);
      g.fill();
      if (card.plate === 'board') {
        g.strokeStyle = 'rgba(90,55,20,0.25)';
        for (let i = 1; i < 7; i++) {
          g.beginPath();
          g.moveTo(S * 0.07, S * (0.12 + i * 0.1));
          g.lineTo(S * 0.93, S * (0.12 + i * 0.1));
          g.stroke();
        }
      }
      if (card.plate === 'basket') {
        g.fillStyle = 'rgba(255,255,255,0.75)';
        g.beginPath();
        g.roundRect(S * 0.12, S * 0.18, S * 0.76, S * 0.64, S * 0.03);
        g.fill();
      }
      break;
    }
    case 'glass':
      g.fillStyle = 'rgba(200,225,240,0.55)';
      g.strokeStyle = '#9ab3c4';
      if (card.view === 'side') {
        g.beginPath();
        g.roundRect(S * 0.3, S * 0.3, S * 0.3, S * 0.52, S * 0.02);
      } else {
        g.beginPath();
        g.arc(c, c, S * 0.42, 0, Math.PI * 2);
      }
      g.fill();
      g.stroke();
      break;
    case 'mug':
      g.fillStyle = '#ffffff';
      g.strokeStyle = '#c9c9c9';
      g.beginPath();
      g.arc(S * 0.4, S * 0.45, S * 0.26, 0, Math.PI * 2);
      g.fill();
      g.stroke();
      break;
    case 'cone':
      g.fillStyle = '#f2ead8';
      g.beginPath();
      g.moveTo(S * 0.3, S * 0.2);
      g.lineTo(S * 0.7, S * 0.2);
      g.lineTo(c, S * 0.85);
      g.closePath();
      g.fill();
      break;
  }
  if (card.view === 'side') {
    // Table line for side views.
    g.strokeStyle = '#8a7a66';
    g.lineWidth = S * 0.008;
    g.beginPath();
    g.moveTo(S * 0.02, S * 0.86);
    g.lineTo(S * 0.98, S * 0.86);
    g.stroke();
  }
  g.restore();
}

function elementShape(g: G, e: PlateElement, S: number) {
  const x = e.x * S,
    y = e.y * S,
    w = e.w * S,
    h = e.h * S;
  g.save();
  g.translate(x, y);
  g.rotate((e.rot * Math.PI) / 180);
  g.beginPath();
  switch (e.shape) {
    case 'circle':
      g.arc(0, 0, Math.min(w, h) / 2, 0, Math.PI * 2);
      break;
    case 'ellipse':
      g.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2);
      break;
    case 'rect':
      g.roundRect(-w / 2, -h / 2, w, h, Math.min(w, h) * 0.15);
      break;
    case 'leaf':
      g.moveTo(-w / 2, 0);
      g.quadraticCurveTo(0, -h, w / 2, 0);
      g.quadraticCurveTo(0, h, -w / 2, 0);
      break;
    case 'drop':
      g.moveTo(-w / 2, h * 0.1);
      g.bezierCurveTo(-w * 0.2, -h / 2, w * 0.3, -h / 2, w / 2, 0);
      g.bezierCurveTo(w * 0.2, h / 2, -w * 0.3, h / 2, -w / 2, h * 0.1);
      break;
    case 'triangle':
      g.moveTo(0, -h / 2);
      g.lineTo(w / 2, h / 2);
      g.lineTo(-w / 2, h / 2);
      g.closePath();
      break;
    case 'stack':
      for (let i = 0; i < 5; i++) g.rect(-w / 2 + (i * w) / 5, -h / 2 + (i % 2) * h * 0.08, w / 7, h * 0.9);
      break;
  }
  if (e.kind === 'space') {
    g.setLineDash([S * 0.012, S * 0.01]);
    g.strokeStyle = '#7a7a7a';
    g.lineWidth = S * 0.004;
    g.stroke();
  } else {
    g.fillStyle = e.color;
    g.fill();
    g.strokeStyle = 'rgba(0,0,0,0.25)';
    g.lineWidth = S * 0.003;
    g.stroke();
  }
  g.restore();
}

function numberBadge(g: G, n: number, x: number, y: number, r: number) {
  g.fillStyle = '#1d1d1d';
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = '#fff';
  g.font = `700 ${r * 1.15}px ${FONT}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(String(n), x, y + r * 0.05);
  g.textAlign = 'left';
}

/** The camera-angle sketch: a plate on a table and a camera at 90°, 45° or 0–15°. */
function angleSketch(g: G, a: CameraAngle, x: number, y: number, s: number) {
  g.save();
  g.translate(x, y);
  g.strokeStyle = '#555';
  g.lineWidth = 3;
  g.beginPath();
  g.moveTo(0, s * 0.8);
  g.lineTo(s, s * 0.8);
  g.stroke();
  g.fillStyle = '#ddd';
  g.beginPath();
  g.ellipse(s / 2, s * 0.76, s * 0.22, s * 0.05, 0, 0, Math.PI * 2);
  g.fill();
  const ang = a === 'overhead' ? 90 : a === '45' ? 45 : 8;
  const r = s * 0.55;
  const cx = s / 2 + Math.cos(((180 - ang) * Math.PI) / 180) * r * (a === 'overhead' ? 0 : 1);
  const cy = s * 0.76 - Math.sin((ang * Math.PI) / 180) * r;
  g.save();
  g.translate(cx, cy);
  g.rotate(((90 - ang) * Math.PI) / 180);
  g.fillStyle = '#1d1d1d';
  g.fillRect(-s * 0.1, -s * 0.07, s * 0.2, s * 0.14);
  g.fillRect(-s * 0.04, s * 0.06, s * 0.08, s * 0.05);
  g.restore();
  g.setLineDash([6, 6]);
  g.beginPath();
  g.moveTo(cx, cy);
  g.lineTo(s / 2, s * 0.76);
  g.stroke();
  g.restore();
}

/** Draws the whole card into a W × H area at (0, 0). */
export function drawCard(
  g: G,
  card: PlatingCard,
  lang: CardLang,
  W = CARD_W,
  H = CARD_H,
  photo?: ImageBitmap | null,
) {
  g.save();
  g.scale(W / CARD_W, H / CARD_H);
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, CARD_W, CARD_H);
  // Header
  g.fillStyle = '#15805d';
  g.fillRect(0, 0, CARD_W, 140);
  g.fillStyle = '#fff';
  g.font = `800 52px ${FONT}`;
  g.textBaseline = 'top';
  g.fillText(lang === 'en' ? card.name.en : card.name.es, 50, 26, CARD_W - 100);
  g.font = `500 28px ${FONT}`;
  if (lang === 'both') g.fillText(card.name.en, 50, 90, CARD_W - 100);
  else g.fillText(lang === 'en' ? 'Plating and photo guide' : 'Guía de emplatado y foto', 50, 92);
  // Diagram
  const S = 720,
    dx = 50,
    dy = 180;
  g.save();
  g.translate(dx, dy);
  g.fillStyle = '#f3efe8';
  g.fillRect(0, 0, S, S);
  plateShape(g, card, S);
  for (const e of card.elements) elementShape(g, e, S);
  card.elements.forEach((e, i) =>
    numberBadge(g, i + 1, e.x * S + e.w * S * 0.42, e.y * S - e.h * S * 0.42, 17),
  );
  g.restore();
  // Legend
  let y = dy;
  const lx = dx + S + 30,
    lw = CARD_W - lx - 50;
  g.font = `700 26px ${FONT}`;
  g.fillStyle = '#111';
  g.fillText(
    lang === 'en' ? 'On the plate' : lang === 'es' ? 'En el plato' : 'En el plato / On the plate',
    lx,
    y,
  );
  y += 44;
  card.elements.forEach((e, i) => {
    numberBadge(g, i + 1, lx + 16, y + 14, 15);
    const yy = para(g, text(e.label, lang), lx + 42, y, lw - 42, 21, '#222', 600, 1.2);
    y = para(g, text(KIND_TEXT[e.kind], lang), lx + 42, yy, lw - 42, 16, '#777', 400, 1.2) + 10;
  });
  // Angle, plate, light
  y = dy + S + 36;
  angleSketch(g, card.angle, dx, y - 10, 150);
  const ix = dx + 175,
    iw = CARD_W - ix - 50;
  g.font = `700 24px ${FONT}`;
  g.fillStyle = '#111';
  g.fillText(lang === 'en' ? 'Camera' : lang === 'es' ? 'Cámara' : 'Cámara / Camera', ix, y);
  y = para(g, text(ANGLE_TEXT[card.angle], lang), ix, y + 32, iw, 22);
  g.font = `700 24px ${FONT}`;
  g.fillStyle = '#111';
  g.fillText(lang === 'en' ? 'Plate' : lang === 'es' ? 'Plato' : 'Plato / Plate', ix, y + 8);
  y = para(g, text(PLATE_TEXT[card.plate], lang), ix, y + 40, iw, 22);
  g.font = `700 24px ${FONT}`;
  g.fillStyle = '#111';
  g.fillText(lang === 'en' ? 'Light' : lang === 'es' ? 'Luz' : 'Luz / Light', ix, y + 8);
  y = para(g, text(card.light, lang), ix, y + 40, iw, 22);
  // Steps + reference photo
  y = Math.max(y, dy + S + 200) + 20;
  const photoW = photo ? 360 : 0;
  const sw = CARD_W - 100 - (photo ? photoW + 30 : 0);
  g.font = `700 26px ${FONT}`;
  g.fillStyle = '#111';
  g.fillText(lang === 'en' ? 'Steps' : lang === 'es' ? 'Pasos' : 'Pasos / Steps', 50, y);
  const photoY = y;
  y += 42;
  // Long own steps (or "both" languages) shrink to stay above the footer instead of running off the card.
  const stepsEnd = (size: number, draw: boolean) => {
    let yy = y;
    // A blank line in the steps box (e.g. a trailing Enter) is not a numbered empty step on the card.
    card.steps
      .filter((s) => (lang === 'both' ? s.es + s.en : s[lang]).trim())
      .forEach((s, i) => {
        if (draw) numberBadge(g, i + 1, 66, yy + 14, 15);
        yy = para(g, text(s, lang), 94, yy, sw - 44, size, '#222', 400, 1.25, draw) + 8;
      });
    return yy;
  };
  let stepSize = 22;
  while (stepSize > 14 && stepsEnd(stepSize, false) > CARD_H - 60) stepSize--;
  y = stepsEnd(stepSize, true);
  // The reference photo keeps its shape: fitted into its column above the footer (it was squashed to
  // the column width when tall, and drawn with a negative height when there was no room).
  const room = CARD_H - photoY - 90;
  if (photo && room > 40) {
    const k = Math.min(photoW / photo.width, room / photo.height);
    const pw = photo.width * k,
      ph = photo.height * k;
    const px = CARD_W - 50 - photoW + (photoW - pw) / 2;
    g.drawImage(photo, px, photoY, pw, ph);
    g.strokeStyle = '#ccc';
    g.strokeRect(px, photoY, pw, ph);
    para(
      g,
      lang === 'en'
        ? 'Reference photo'
        : lang === 'es'
          ? 'Foto de referencia'
          : 'Foto de referencia / Reference photo',
      px,
      photoY + ph + 6,
      photoW,
      16,
      '#777',
    );
  }
  // Footer
  g.fillStyle = '#999';
  g.font = `400 16px ${FONT}`;
  g.fillText('EAT Photo Booth', 50, CARD_H - 40);
  g.restore();
}
